import React from 'react';
import { motion } from 'framer-motion';
import { FaCode, FaUsers, FaLightbulb } from 'react-icons/fa';

const About = () => {
  const highlights = [
    {
      icon: FaCode,
      title: "Technical Expertise",
      description: "Specialized in PHP, Laravel, WordPress, and modern web development frameworks"
    },
    {
      icon: FaUsers,
      title: "Team Leadership",
      description: "Experience in team handling, task distribution, and client communication"
    },
    {
      icon: FaLightbulb,
      title: "Problem Solving",
      description: "Proven track record in identifying and solving complex technical issues"
    }
  ];

  return (
    <section id="about" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-4">
            About Me
          </h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto"></div>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative z-10">
              <img
                src="https://images.unsplash.com/photo-1626362814904-d27009a10da7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxkZXZlbG9wZXIlMjBjb2Rpbmd8ZW58MHx8fHwxNzU0MTM4MzEwfDA&ixlib=rb-4.1.0&q=85"
                alt="Coding workspace"
                className="rounded-lg shadow-md w-full h-80 object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 w-full h-full bg-blue-600 rounded-lg z-0"></div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="space-y-5">
              <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900">
                Professional Summary
              </h3>
              <p className="text-gray-700 text-base md:text-lg leading-8">
                I am a Senior PHP/Laravel Developer with over 11 years of experience designing and building scalable web applications, SaaS platforms, and enterprise backend systems.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-8">
                I specialize in architecting high-performance Laravel applications, building API-driven systems, and optimizing database-intensive platforms used by thousands of users.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-8">
                Throughout my career I have worked across industries including construction management, healthcare, SaaS media platforms, education systems, and subscription commerce platforms.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-8">
                My focus is on building maintainable systems using clean architecture principles, modular Laravel applications, and scalable infrastructure.
              </p>
            </div>

          </motion.div>
        </div>

        {/* Highlights */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 mt-16"
        >
          {highlights.map((highlight, index) => (
            <div
              key={index}
              className="bg-white p-8 rounded-xl shadow-md text-center hover:-translate-y-2 transition-all duration-300"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <highlight.icon className="text-blue-600 text-2xl" />
              </div>
              <h4 className="text-xl font-semibold text-gray-900 mb-4">
                {highlight.title}
              </h4>
              <p className="text-gray-600 leading-relaxed">
                {highlight.description}
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
